import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import TopBar from "@/components/TopBar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, ShieldCheck } from "lucide-react";

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "collaborator" });

  const load = () => {
    api.get("/users").then(({ data }) => setUsers(data)).catch(() => setUsers([]));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, []);

  const create = async () => {
    if (!form.name.trim() || !form.email.trim() || !form.password) {
      toast.error("Compila tutti i campi");
      return;
    }
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("Utente creato");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "collaborator" });
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/users/${id}`);
      toast.success("Utente eliminato");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="min-h-screen bg-background grain">
      <TopBar />
      <main className="max-w-[1100px] mx-auto px-6 py-12">
        <div className="flex items-end justify-between mb-10 fade-up">
          <div>
            <p className="text-xs font-semibold tracking-[0.2em] uppercase text-muted-foreground">Gestione accessi</p>
            <h1 className="font-heading text-4xl font-light tracking-tight mt-3">Utenti</h1>
            <p className="text-sm text-muted-foreground mt-3 max-w-lg">
              Crea gli account dei collaboratori (clienti, geometri, imprese, falegnami…) e assegnali ai progetti.
            </p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="create-user-button"
                className="rounded-none h-11 gap-2 bg-foreground text-background hover:bg-foreground/90">
                <Plus className="h-4 w-4" /> Nuovo utente
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-none sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading font-light text-2xl">Nuovo utente</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">Nome</Label>
                  <Input data-testid="user-name-input" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="rounded-none focus-visible:ring-foreground" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">Email</Label>
                  <Input data-testid="user-email-input" type="email" value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="rounded-none focus-visible:ring-foreground" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">Password</Label>
                  <Input data-testid="user-password-input" value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="rounded-none focus-visible:ring-foreground" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">Ruolo</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="user-role-select" className="rounded-none focus:ring-foreground">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="collaborator">Collaboratore</SelectItem>
                      <SelectItem value="admin">Amministratore</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" className="rounded-none" onClick={() => setOpen(false)}>Annulla</Button>
                <Button data-testid="user-save-button" onClick={create} disabled={saving}
                  className="rounded-none bg-foreground text-background hover:bg-foreground/90">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea utente"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <div className="border border-border bg-white fade-up">
          {users === null ? (
            <div className="py-16 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-xs uppercase tracking-[0.1em]">Nome</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.1em]">Email</TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.1em]">Ruolo</TableHead>
                  <TableHead className="w-16"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-medium">{u.name}</TableCell>
                    <TableCell className="text-muted-foreground">{u.email}</TableCell>
                    <TableCell>
                      {u.role === "admin" ? (
                        <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider">
                          <ShieldCheck className="h-3.5 w-3.5" /> Admin
                        </span>
                      ) : (
                        <span className="text-xs uppercase tracking-wider text-muted-foreground">Collaboratore</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {u.role !== "admin" && (
                        <Button variant="ghost" size="icon" data-testid={`delete-user-${u.id}`}
                          onClick={() => remove(u.id)} className="rounded-none h-8 w-8 text-muted-foreground hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>
    </div>
  );
}
